import type {
    FileViewModel,
    IndividualReport,
    IndividualReportCreationParams,
    IndividualReportUpdatingParams,
} from 'chaika-frontend-types/frontend-cms';
import { FileEntityType } from 'chaika-enums';

import { IndividualReportsApi, FileApi } from '@api';

import { parseFileName } from '@utils/parseFileName';

import { store } from '@store';

import { IndividualReportsStore } from '../store/IndividualReportsStore';
import { IndividualReportTemplateStrategy } from './IndividualReportTemplateStrategy';

class IndividualReportsService {
    private readonly store: IndividualReportsStore = store.individualReportsStore;
    private readonly fileTasks: Map<string, FileViewModel[]> = new Map();

    public async load(legalEntityId: string): Promise<void> {
        const reports = await IndividualReportsApi.getReports(legalEntityId);
        this.store.addIndividualReport(...reports);

        const reportIds = reports.map(it => it.id);
        const files =
            reportIds.length > 0 ? await FileApi.getFiles(reportIds, FileEntityType.LegalEntityIndividualReport) : [];

        this.store.setFiles(files);
    }

    public async create(individualReport: IndividualReportCreationParams): Promise<void> {
        await IndividualReportsApi.create(individualReport);
        this.store.addIndividualReport({
            ...individualReport,
            createdAt: new Date().toISOString(),
        });

        if (this.fileTasks.has(individualReport.id)) {
            const tasks = this.fileTasks.get(individualReport.id) ?? [];
            await Promise.all(tasks.map(async file => this.createFile(individualReport.id, file, false)));
            this.fileTasks.delete(individualReport.id);
        }
    }

    public async update(individualReportId: string, individualReport?: IndividualReportUpdatingParams): Promise<void> {
        if (individualReport) {
            await IndividualReportsApi.update(individualReportId, individualReport);
            this.store.updateIndividualReport(individualReportId, individualReport);
        }
    }

    public async createFile(
        individualReportId: string,
        fileAsset: FileViewModel,
        addToStore: boolean = true,
    ): Promise<void> {
        await FileApi.addEntityFile(
            individualReportId,
            FileEntityType.LegalEntityIndividualReport,
            parseFileName(fileAsset.path, false),
        );

        if (addToStore) {
            this.store.addFile(individualReportId, fileAsset);
        }
    }

    public async deleteFile(individualReportId: string, path: string): Promise<void> {
        await FileApi.deleteEntityFile(
            individualReportId,
            FileEntityType.LegalEntityIndividualReport,
            parseFileName(path, false),
        );

        this.store.deleteFile(individualReportId, path);
    }

    public async addFileTask(individualReportId: string, fileAsset: FileViewModel): Promise<void> {
        const currentTasks = this.fileTasks.get(individualReportId) ?? [];
        this.fileTasks.set(individualReportId, currentTasks.concat(fileAsset));

        this.store.addFile(individualReportId, fileAsset);
    }

    public async renderTemplate(individualReport: IndividualReport, params: object): Promise<Blob> {
        const data = IndividualReportTemplateStrategy.getBuilder()
            .selectIndividualReport(individualReport)
            .getTemplateData(params);

        return IndividualReportsApi.renderReport(individualReport.id, data);
    }
}

export const individualReportsService = new IndividualReportsService();
